import { Box, makeStyles, Theme } from "@material-ui/core";
import { ProductStackCard } from "./ProductStackview";
import { AsyncActionState } from "legacy-components/form/action/AsyncAction";
import React, { useEffect, useState } from "react";
import { withViewController } from "modus/utilities/ViewController";
import { useProductListController } from "./ProductList.controller";
import { IProduct } from "pages/home/consumer/service/ProductService";
import { AxiosResponse } from "axios";
import _ from "lodash";
import { Skeleton } from "@material-ui/lab";
import {
  APIErrorMessage,
  ErrorMessage,
} from "modus/components/error-message/ErrorMessage";

const useStyles = makeStyles((theme: Theme) => ({
  productListContainer: {
    marginTop: theme.spacing(2),
    maxHeight: "calc(100vh - 325px)",
    overflowY: "auto",
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "100%"
  }
}));

export interface ProductListViewModel {
  asyncResponse?: AsyncActionState<AxiosResponse<IProduct[]> | null> | undefined;
  filteredProducts?: IProduct[];
  isGuest?: boolean;
}

interface StackSkeletonProps<Type> {
  response?: AsyncActionState<Type>;
  count: number;
}
export const StackSkeleton = <Type,>({
  response,
  count = 4,
}: StackSkeletonProps<Type>) => (
  <>
    {response?.loading &&
      new Array(count).fill(1).map((num, index) => (
        <Box mb={"2px"} key={index}>
          <Skeleton
            animation="wave"
            variant="rect"
            height={"70px"}
            width="100%"
          />
        </Box>
      ))}
  </>
);
export const ProductListView: React.FC<ProductListViewModel> = ({
  asyncResponse,
  filteredProducts,
  isGuest
}) => {
  const classes = useStyles();

  // If loading, show the skeleton
  if (asyncResponse?.loading) {
    return (
      <Box className={classes.productListContainer} data-testid="loading-skeleton">
        <StackSkeleton count={8} response={{loading: true} as any} />
      </Box>
    );
  }

  // If we have filteredProducts array but it's empty, show "No matching products" message
  if (filteredProducts && filteredProducts.length === 0) {
    return (
      <Box className={classes.productListContainer} data-testid="empty-results">
        <Box p={1} textAlign="center">
          <ErrorMessage message={"No matching products found"} />
        </Box>
      </Box>
    );
  }


  if (!filteredProducts) {
    if (asyncResponse?.value?.data) {
      // If the data array exists but is empty, show "No products found" message
      if (asyncResponse.value.data.length === 0) {
        return (
          <Box className={classes.productListContainer} data-testid="empty-data">
            <Box p={1} textAlign="center">
              <ErrorMessage message={"No products found"} />
            </Box>
          </Box>
        );
      }
      
      // If there are products in the response, render them
      return (
        <Box className={classes.productListContainer} data-testid="direct-products">
          {(asyncResponse.value.data as IProduct[]).map((product: IProduct) => {
            return product && (
              <ProductStackCard 
                key={`product-${product.id || product.name}`} 
                {...product} 
                isGuest={isGuest} 
              />
            );
          })}
        </Box>
      );
    }
    
    // If we have neither filteredProducts nor asyncResponse data, show skeleton
    return (
      <Box className={classes.productListContainer} data-testid="empty-data">
        <StackSkeleton count={3} response={{loading: true} as any} />
      </Box>
    );
  }

  // Main content with data
  return (
    <Box className={classes.productListContainer} data-testid="filtered-products">
      {/* List all products with a unique identifier as key */}
      {filteredProducts?.map((product: IProduct) => {
        return product && (
          <ProductStackCard 
            key={`product-${product.id || product.name}`} 
            {...product} 
            isGuest={isGuest} 
          />
        );
      })}
      
      {/* Handle error state */}
      {asyncResponse?.error && (
        <APIErrorMessage error={asyncResponse?.errorValue} />
      )}
    </Box>
  );
};

export const ProductList = withViewController(
  ProductListView,
  useProductListController
);
